import {
  type SeverityLevel,
  captureException,
  captureMessage,
} from '@sentry/react';

type ExceptionContext = Parameters<typeof captureException>['1'];

type MessageContext = Exclude<
  Parameters<typeof captureMessage>['1'],
  SeverityLevel
>;

export const monitoringMessageHelper = (
  message: string,
  context?: MessageContext
) => {
  captureMessage(message, {
    level: 'warning',
    ...context,
  });
};

export const monitoringExceptionHelper = (
  error: any,
  context?: ExceptionContext
) => {
  captureException(error, context);
};

type UserpilotBusinessConfig =
  | {
      appId?: unknown;
      __kizen_clean_config?: {
        appId?: unknown;
        disableKizenConfiguration?: unknown;
      };
    }
  | null
  | undefined;

const USERPILOT_APP_ID_PLACEHOLDER = '{your_app_id}';

const normalizeUserpilotAppId = (value: unknown) => {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();

  return trimmed && trimmed !== USERPILOT_APP_ID_PLACEHOLDER ? trimmed : null;
};

export const getUserpilotAppIds = (
  businessConfig: UserpilotBusinessConfig,
  internalAppId: string | undefined | null
): string[] => {
  const cleanConfig = businessConfig?.__kizen_clean_config;

  const internalId = normalizeUserpilotAppId(internalAppId);

  // We support both shapes from the plugin config so nobody is forced to migrate immediately.
  const externalId =
    normalizeUserpilotAppId(cleanConfig?.appId) ??
    normalizeUserpilotAppId(businessConfig?.appId);

  const appIds = [
    cleanConfig?.disableKizenConfiguration ? null : internalId,
    externalId,
  ];

  return Array.from(new Set(appIds.filter((id): id is string => Boolean(id))));
};

export const haveSameUserpilotAppIds = (
  appIds: string[],
  otherAppIds: string[]
): boolean => {
  const ids = new Set(appIds);
  const otherIds = new Set(otherAppIds);

  return (
    ids.size === otherIds.size &&
    Array.from(ids).every((id) => otherIds.has(id))
  );
};
